<template>
  <section>
    <span data-test="infotext">{{ $t('cita.infotext1') }}</span>
    <VProgress class="offset-top-2 offset-bottom-2" :steps="steps" active="cita.yourphonenumber" />
    <span>{{ $t('cita.identtext1') }}</span>

    <VForm v-if="form" :form="form" @submit="onNext">
      <VInput
        name="msisdn"
        :label="$t('cita.yourphonenumber')"
        :hint="$t('errorcodes.MSISDN_FORMAT_ERROR')"
        :placeholder="$t('cita.inputphonenumber')"
        required
      />
      <div class="offset-top-2 text-right">
        <transition name="fade" mode="out-in" appear>
          <VSpinner v-if="isBusy" slim class="pull-left" />
        </transition>
        <VButton tag="a" class="offset-left-1 offset-bottom-1 btn btn-default" label="cita.abort" @click.prevent="onAbort" />
        <VButton type="submit" class="offset-left-1 offset-bottom-1 btn btn-default btn-brand" label="cita.continue" :disabled="isBusy" />
      </div>
    </VForm>
  </section>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import TitleMixin from '@/mixins/title';
import CitaStepsMixin from '@/mixins/citaSteps';
import VSpinner from '@/components/Spinner';
import VForm from '@/components/Form/Form';
import VInput from '@/components/Form/Input';
import Proxy from '@/proxies/CitaProxy';
import { safeNavigate } from '@/utils/safeUrl';
import { focusFirstError, formattedPhone, hasMaxLength, hasMinLength, initFields, match } from '@/utils/form';
import { CITA_AUTH } from '@/router/route-types';

export default {
  name: 'CitaIdentify',

  components: {
    VForm,
    VInput,
    VSpinner,
    VProgress: () => import('@/components/Progress'),
    VButton: () => import('@/components/Button'),
  },

  mixins: [TitleMixin, CitaStepsMixin],

  data() {
    return {
      form: null,
      isBusy: false,
    };
  },

  computed: {
    ...mapState('cita', ['msisdnMinLength', 'msisdnMaxLength', 'msisdnCountryCode', 'refUrl']),
    ...mapGetters('cita', ['getPageTypeWebtrekk']),
  },

  created() {
    this.form = initFields({
      name: 'msisdn',
      validators: [
        hasMinLength(this.msisdnMinLength),
        hasMaxLength(this.msisdnMaxLength),
        { handler: match(new RegExp(`^${this.msisdnCountryCode}`, 'g')), hint: 'errorcodes.MSISDN_AREA_CODE_ERROR' },
      ],
      filters: [formattedPhone(this.msisdnCountryCode)],
    });
    this.$webtrekk.viewPage(`cita.${this.getPageTypeWebtrekk}.handynummer`);
  },

  methods: {
    async onAbort() {
      await this.$webtrekk.link('cita.abort', this.refUrl);
      // SECURITY: safeNavigate refuses non-http(s) URLs (blocks javascript:/data: XSS).
      safeNavigate(this.refUrl);
    },

    async onNext({ values, form }) {
      try {
        this.isBusy = true;
        await new Proxy().msisdnVerify(values.msisdn);
        this.$store.dispatch('cita/setMsisdn', values.msisdn); // we intentionally don't store the msisdn. will not survive f5
        this.$router.replace({ name: CITA_AUTH });
        this.$webtrekk.link('cita.continue', CITA_AUTH);
      } catch (error) {
        const code = error?.data?.code;
        if (['MSISDN_FORMAT_ERROR', 'TPV_START_SUBSCRIBER_UNKNOWN'].includes(code)) {
          form.msisdn.setInvalid();
        } else if (code === 'MSISDN_AREA_CODE_ERROR') {
          form.msisdn.setInvalid(this.$t('errorcodes.MSISDN_AREA_CODE_ERROR'));
        } else {
          throw error;
        }
        focusFirstError(form);
        this.isBusy = false;
      }
    },
  },
};
</script>
